import { type RefObject, useRef } from 'react';

import { type DocNode } from '@atlaskit/adf-schema';
import { reduce, traverse } from '@atlaskit/adf-utils/traverse';
import { type ADFEntity } from '@atlaskit/adf-utils/types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { ResponseObject } from './types';

type StreamEvent = Extract<ResponseObject, { type: 'stream' }>;

interface Ramp {
	initialDelaySec: number;
	stepDelaySec: number;
	growthFactor: number;
}

interface Config {
	emit?: (evt: ResponseObject) => void;
	tickMs: number;
	initialCharsPerTick: number;
	maxCharsPerTick: number;
	backlogRampThreshold: number;
	ramp: Ramp;
}

export class StreamFlowPacer {
	readonly push = (evt: ResponseObject): void => {
		if (evt.type === 'stream') {
			this.streamEvt = evt;
		} else {
			this.terminalEvt = evt;
		}
	};

	constructor(private readonly cfg: Config) {
		this.charsPerTick = cfg.initialCharsPerTick;
	}

	setEmit(emit: Config['emit']) {
		this.cfg.emit = emit;
	}

	/**
	 * you need to call setEmit before calling start
	 *
	 * @example
	 * streamFlowPacer.setEmit();
	 * streamFlowPacer.start();
	 */
	start() {
		if (this.cfg.emit) {
			this.timer = setInterval(() => this.tick(), this.cfg.tickMs);
		}
	}

	pause() {
		this.paused = true;
		this.stop();
	}

	resume() {
		this.paused = false;
		this.start();
	}

	dispose(): void {
		this.stop();
	}

	private paused = false;
	private charsPerTick: number;
	private delivered = 0;
	private ticksSinceRamp = 0;
	private rampedOnce = false;
	private streamEvt: StreamEvent | null = null;
	private terminalEvt: ResponseObject | null = null;
	private timer: ReturnType<typeof setInterval> | null = null;

	private stop() {
		if (this.timer) {
			clearInterval(this.timer);
		}
	}

	private tick() {
		if (this.paused || !this.streamEvt) {
			return;
		}

		this.maybeRamp();
		this.deliverChunk();
		this.maybeFlushTerminal();
	}

	private deliverChunk() {
		if (!this.streamEvt) {
			return;
		}

		const total = this.semanticLen(this.streamEvt);
		if (this.delivered >= total) {
			return;
		}

		const limit = Math.min(this.delivered + this.charsPerTick, total);
		const slice = this.sliceStreamEvt(this.streamEvt, limit);
		if (!slice) {
			return;
		}

		this.cfg.emit?.(slice);
		this.delivered = limit;
	}

	private canFlushTerminal(): boolean {
		if (!this.terminalEvt) {
			return false;
		}

		// For "complete" we must wait until every char is flushed
		if (this.terminalEvt.type === 'complete') {
			return this.backlog() === 0;
		}

		// All other terminal kinds (error, abort, etc.) bypass the backlog
		return true;
	}

	private maybeFlushTerminal(): void {
		if (!this.canFlushTerminal()) {
			return;
		}

		if (this.terminalEvt) {
			this.cfg.emit?.(this.terminalEvt);
			this.dispose();
		}
	}

	private maybeRamp() {
		if (!this.shouldRamp()) {
			this.ticksSinceRamp++;
			return;
		}
		this.charsPerTick = Math.min(
			Math.ceil(this.charsPerTick * this.cfg.ramp.growthFactor),
			this.cfg.maxCharsPerTick,
		);
		this.ticksSinceRamp = 0;
		this.rampedOnce = true;
	}

	private shouldRamp() {
		const wait = this.secondsToTicks(
			this.rampedOnce ? this.cfg.ramp.stepDelaySec : this.cfg.ramp.initialDelaySec,
		);
		return (
			this.backlog() > this.cfg.backlogRampThreshold &&
			this.ticksSinceRamp >= wait &&
			this.charsPerTick < this.cfg.maxCharsPerTick
		);
	}

	private backlog() {
		if (!this.streamEvt) {
			return 0;
		}
		return this.semanticLen(this.streamEvt) - this.delivered;
	}

	private secondsToTicks(sec: number) {
		return (sec * 1000) / this.cfg.tickMs;
	}

	private semanticLen(evt: StreamEvent) {
		const adfChars = this.adfTextLen(evt.adfResponse);
		return adfChars || evt.markdown?.length || 0;
	}

	private sliceStreamEvt(evt: StreamEvent, chars: number): StreamEvent | null {
		if (evt.adfResponse) {
			return { ...evt, adfResponse: this.truncateAdf(evt.adfResponse, chars) };
		}
		if (evt.markdown?.length) {
			return { ...evt, markdown: evt.markdown.slice(0, chars) };
		}
		return null;
	}

	private adfTextLen(root: ADFEntity | null | undefined): number {
		if (!root) {
			return 0;
		}
		return reduce<number>(
			root,
			(sum, node) =>
				node.type === 'text' && typeof node.text === 'string' ? sum + node.text.length : sum,
			0,
		);
	}

	private truncateAdf(root: ADFEntity, budget: number): DocNode {
		let remaining = Math.max(0, budget);
		const clone = structuredClone(root);
		return traverse(clone, {
			any(node) {
				if (remaining === 0) {
					return false;
				}
				if (node.type !== 'text' || typeof node.text !== 'string') {
					return;
				}

				if (node.text.length <= remaining) {
					remaining -= node.text.length;
					return;
				}
				const text = node.text.slice(0, remaining);
				remaining = 0;
				return { ...node, text };
			},
		}) as DocNode;
	}
}

export function createStreamFlowPacer(): StreamFlowPacer {
	return new StreamFlowPacer({
		tickMs: 41, // 24 fps to avoid issues with RAF and scroll listeners
		initialCharsPerTick: 8,
		maxCharsPerTick: 1000,
		backlogRampThreshold: 100,
		ramp: { initialDelaySec: 6, stepDelaySec: 3, growthFactor: 1.5 },
	});
}

export function useStreamFlowPacerRef(): RefObject<StreamFlowPacer> {
	const streamFlowPacerRef = useRef(
		fg('platform_editor_ai_in_document_streaming') ? createStreamFlowPacer() : null,
	);
	return streamFlowPacerRef;
}
